/**
 * practiceStore · LocalStorage Phase 1 implementation.
 * Per `12-sandbox-architecture.md` §5.
 *
 * Phase 2 swap-in: BackendPracticeStore reading from REST endpoints.
 * Adapter interface keeps components unchanged on swap.
 */

import type { AuthorDraft, PracticeProgress, PracticeStore, RankStats } from '@/types';
import type { RankType } from '@shared/schemas';

/**
 * Map scenario `rank` field prefix → RankType counter key.
 * W3.1 extension 2026-05-04 · was 2-rank (detective/author) · now full coverage.
 *
 * Block 1: detective-* / author-* prefixes (existing).
 * Block 2: api-master-* / sql-detective-* / security-watcher-* (W3.1 catalog).
 * Block 3: automation-architect-* / ci-wrangler-* (W3.1 catalog).
 * Block 4: ai-qa-master-* (Wave 4 future).
 *
 * CTFL is NOT scenario-driven · lives in mockHistory · matchRank handles separately.
 *
 * Sorted longest-prefix-first to prevent ambiguity (e.g. `sql-detective-junior`
 * must match `sql-detective` before falling through to `detective`).
 */
const SCENARIO_RANK_PREFIXES: ReadonlyArray<RankType> = [
  'automation-architect',
  'security-watcher',
  'sql-detective',
  'ai-qa-master',
  'api-master',
  'ci-wrangler',
  'detective',
  'author',
] as const;

const KEY_PREFIX = 'qastudio.v1.practice';
const progressKey = (scenarioId: string) => `${KEY_PREFIX}.${scenarioId}.progress`;
const draftKey = (scenarioId: string) => `${KEY_PREFIX}.${scenarioId}.draft`;

const safeParse = <T>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export class LocalStoragePracticeStore implements PracticeStore {
  async getProgress(scenarioId: string): Promise<PracticeProgress | null> {
    if (typeof window === 'undefined') return null;
    return safeParse<PracticeProgress>(window.localStorage.getItem(progressKey(scenarioId)));
  }

  async saveProgress(progress: PracticeProgress): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(progressKey(progress.scenarioId), JSON.stringify(progress));
    } catch {
      /* quota exceeded — fail soft */
    }
  }

  async getAuthorDraft(scenarioId: string): Promise<AuthorDraft | null> {
    if (typeof window === 'undefined') return null;
    return safeParse<AuthorDraft>(window.localStorage.getItem(draftKey(scenarioId)));
  }

  async saveAuthorDraft(draft: AuthorDraft): Promise<void> {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(draftKey(draft.scenarioId), JSON.stringify(draft));
    } catch {
      /* fail soft */
    }
  }

  async getAllProgress(): Promise<PracticeProgress[]> {
    if (typeof window === 'undefined') return [];
    const out: PracticeProgress[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k || !k.startsWith(KEY_PREFIX) || !k.endsWith('.progress')) continue;
      const parsed = safeParse<PracticeProgress>(window.localStorage.getItem(k));
      if (parsed) out.push(parsed);
    }
    return out;
  }

  async getRankStats(): Promise<RankStats> {
    const all = await this.getAllProgress();
    const stats: RankStats = {};
    for (const p of all) {
      if (!p.completedAt) continue;
      if (!p.rank) continue;
      // Rank inferred via prefix match. SCENARIO_RANK_PREFIXES is pre-sorted
      // longest-first to prevent ambiguity (e.g. `sql-detective-junior`
      // matches `sql-detective` and breaks before reaching `detective`).
      for (const prefix of SCENARIO_RANK_PREFIXES) {
        if (p.rank.startsWith(prefix)) {
          stats[prefix] = (stats[prefix] ?? 0) + 1;
          break;
        }
      }
    }
    return stats;
  }
}

let _store: PracticeStore | null = null;

export function getPracticeStore(): PracticeStore {
  if (!_store) _store = new LocalStoragePracticeStore();
  return _store;
}

/** For tests only. */
export function resetPracticeStore(): void {
  _store = null;
}
